import { useEffect, useRef, useState } from "react";
import { useResetPasswordMutation } from "@dealer-center/ddc-pages-configuration-fetch";
import { useWriteAnnouncer } from "../StatusRegion/useWriteAnnouncer";
import { resetErrorMessage } from "../writeErrorMessages";

export interface UseResetPasswordActionOptions {
  userId?: string;
  accountId?: string;
  /** Selects the failure copy's register: first person only when the subject is the operator. */
  isSubjectTheOperator?: boolean;
}

/**
 * The reset-password dialog's state and the reset itself, shared by both pages' header actions.
 * Must be called inside `StatusRegion` (see `useWriteAnnouncer`).
 *
 * **An outcome is shown in the dialog only if that dialog is still the one open, for the same
 * user.** Cancel stays live mid-reset, so the operator can close (and reopen) the dialog or select
 * someone else before the reset settles; that outcome is then announced and toasted instead, never
 * shown in a dialog it no longer belongs to, and never dropped.
 *
 * Password hygiene: `password` goes only to the mutation — never into state, logs or analytics —
 * and `reset()` on settle lets the hook's `gcTime: 0` drop the password-bearing `variables`.
 */
export const useResetPasswordAction = ({
  userId,
  accountId,
  isSubjectTheOperator = false
}: UseResetPasswordActionOptions) => {
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [resetError, setResetError] = useState<string | undefined>();
  const resetPassword = useResetPasswordMutation();
  const announcer = useWriteAnnouncer();
  // A ref, not `isPending`: set synchronously, so a second confirm cannot slip in before re-render.
  const isResetInFlightRef = useRef(false);
  // Bumped on every open and close, so an outcome can tell whether its dialog is still the open one.
  const dialogSessionRef = useRef(0);
  const userIdRef = useRef(userId);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const handleResetOpen = () => {
    dialogSessionRef.current += 1;
    setResetError(undefined);
    setIsResetOpen(true);
  };

  const handleResetClose = () => {
    dialogSessionRef.current += 1;
    setIsResetOpen(false);
    setResetError(undefined);
  };

  const handleResetConfirm = (password: string) => {
    if (isResetInFlightRef.current || !userId || !accountId) {
      return;
    }

    const session = dialogSessionRef.current;
    const resetUserId = userId;
    const isOwnDialogOpen = () =>
      dialogSessionRef.current === session && userIdRef.current === resetUserId;

    isResetInFlightRef.current = true;
    setResetError(undefined);
    announcer.clear();
    resetPassword.mutate(
      { userId: resetUserId, accountId, password },
      {
        onSuccess: () => {
          if (isOwnDialogOpen()) {
            setIsResetOpen(false);
          }
          announcer.success("UM_PW_RESET_SUCCESS");
        },
        onError: error => {
          const text = resetErrorMessage(error, { isSubjectTheOperator });
          if (isOwnDialogOpen()) {
            setResetError(text);
            return;
          }
          announcer.failure(text);
        },
        onSettled: () => {
          isResetInFlightRef.current = false;
          resetPassword.reset();
        }
      }
    );
  };

  return {
    isResetOpen,
    resetError,
    isResetting: resetPassword.isPending,
    handleResetOpen,
    handleResetClose,
    handleResetConfirm
  };
};
